import { ChevronDown, ChevronRight } from "lucide-react";
import type { AnalysisEnvelope } from "../api/types";
import { BiasBadge, ConvictionBar } from "../components/BiasBadge";
import { clockTime, percent } from "../components/format";
import { AnalysisExpansion } from "./AnalysisExpansion";
import type { TapeRunGroup } from "./types";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface RunSnapshotCardProps {
  group: TapeRunGroup;
  expanded: boolean;
  selected: boolean;
  highlighted?: boolean;
  previousSnapshot?: AnalysisEnvelope | null;
  onToggle: (snapshotId: string) => void;
  onClusterJump: (clusterId: string) => void;
}

export function RunSnapshotCard({
  group,
  expanded,
  selected,
  highlighted = false,
  previousSnapshot,
  onToggle,
  onClusterJump,
}: RunSnapshotCardProps) {
  const { snapshot } = group;
  const bias = snapshot.body?.directional_bias;
  const previousBias = previousSnapshot?.body?.directional_bias;
  const conviction = bias?.conviction ?? 0;

  return (
    <Card
      className={cn(
        "py-0 gap-0 border-l-4 border-l-primary bg-primary/5 transition-all",
        selected && "ring-2 ring-primary",
        highlighted && "ring-2 ring-amber-500",
      )}
      aria-expanded={expanded}
      data-snapshot-id={snapshot.id}
      data-tape-id={snapshot.id}
    >
      <button
        className="w-full text-start p-3 flex items-center gap-3"
        onClick={() => onToggle(snapshot.id)}
        type="button"
      >
        {expanded ? <ChevronDown className="h-4 w-4 text-primary" /> : <ChevronRight className="h-4 w-4 text-primary" />}
        <span className="text-[10px] uppercase tracking-wide font-semibold text-primary">Analysis</span>
        <span className="text-xs font-mono text-muted-foreground">
          <time dateTime={snapshot.generated_at}>{clockTime(snapshot.generated_at)}</time>
        </span>
        <span className="flex items-center gap-1.5">
          {previousBias ? (
            <>
              <BiasBadge bias={previousBias} />
              <span className="text-xs text-muted-foreground">→</span>
            </>
          ) : null}
          <BiasBadge bias={bias} />
        </span>
        <span className="flex items-center gap-2 ms-auto">
          <ConvictionBar value={conviction} showLabel={false} />
          <span className="text-xs text-muted-foreground font-mono">{percent(conviction)}</span>
        </span>
      </button>

      {expanded ? <AnalysisExpansion analysis={snapshot} onClusterJump={onClusterJump} /> : null}
    </Card>
  );
}
